import Modal from "@/components/common/ui/modal";
import { useOrganizationContext } from "@/context";
import { CREATE_ORGANIZATION } from "@/gql/organization";
import { IOrganization } from "@/types/organization";
import { storeSelectedTeam } from "@/utils/common";
import { useMutation } from "@apollo/client";
import {
  ICreateOrganizationMutation,
  ICreateOrganizationMutationVariables,
} from "@shared/common/types/api-private/generated/graphql";
import { getStringByteLength } from "@shared/common/utils/value";
import { Form, Input } from "antd";
import { useRouter } from "next/router";
import { ChangeEvent } from "react";
import toast, { useToasterStore } from "react-hot-toast";

interface IAddTeamModalProps {
  open: boolean;
  onClose: () => void;
}

const LENGTH_LIMIT_MESSAGE =
  "팀명은 최대 60byte(한글 30자)를 넘길 수 없습니다.";

const AddTeamModal: React.FC<IAddTeamModalProps> = ({ open, onClose }) => {
  const { toasts } = useToasterStore();

  const [form] = Form.useForm();

  const teamName = Form.useWatch("teamName", form);

  const router = useRouter();

  const { organizations, setOrganizations, reload } = useOrganizationContext();

  // 팀 생성
  const [createTeam, { loading: createLoading }] = useMutation<
    ICreateOrganizationMutation,
    ICreateOrganizationMutationVariables
  >(CREATE_ORGANIZATION, {
    onError: (err) => {
      toast(
        err
          ? err.message
          : "팀 생성에 실패했습니다. 잠시 후 다시 시도해 보세요."
      );
    },
    onCompleted: (res) => {
      setOrganizations([
        ...(organizations ?? []),
        // TODO: remove "as"
        res.createOrganization as IOrganization,
      ]);

      if (res.createOrganization?.slug) {
        storeSelectedTeam({ slug: res.createOrganization?.slug });
      }

      toast("새로운 팀을 생성했습니다.");
      // session update
      const event = new Event("visibilitychange");
      document.dispatchEvent(event);

      reload();
      onClose();
      const { pathname, query } = router;
      router.replace({
        pathname,
        query: { ...query, org_slug: res.createOrganization?.slug },
      });
    },
  });

  const hasSameToastMessage = (message: string) => {
    return toasts.some((toast) => toast.message === message);
  };

  const handleTeamNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    const targetEle = e.currentTarget;
    const targetValue = e.currentTarget.value;
    const lastSelectionEnd = targetEle.selectionEnd;
    if (/^\s+/.test(targetValue)) {
      targetEle.value = String(teamName);
      form.setFieldValue("teamName", teamName);
      if (lastSelectionEnd) {
        targetEle.selectionEnd = lastSelectionEnd - 1;
      }
      return;
    }

    if (/\s\s/.test(targetValue)) {
      targetEle.value = String(teamName);
      form.setFieldValue("teamName", teamName);
      if (lastSelectionEnd) {
        targetEle.selectionEnd = lastSelectionEnd - 1;
      }
      return;
    }

    if (getStringByteLength(targetValue) > 60) {
      if (!hasSameToastMessage(LENGTH_LIMIT_MESSAGE)) {
        toast(LENGTH_LIMIT_MESSAGE);
      }

      targetEle.value = String(teamName);
      form.setFieldValue("teamName", teamName);
      if (lastSelectionEnd) {
        targetEle.selectionEnd = lastSelectionEnd - 1;
      }
    }
  };

  const handleAddTeam = () => {
    form.validateFields().then((values) => {
      createTeam({
        variables: {
          object: { name: values.teamName, onboardStep: 10 },
        },
      });
    });
  };

  return (
    <Modal
      open={open}
      title="팀 추가"
      onClose={() => onClose()}
      onOk={() => handleAddTeam()}
      okText="저장"
      okDisabled={!teamName}
      okLoading={createLoading}
      size="small"
    >
      <Form
        initialValues={{ teamName: "" }}
        form={form}
        requiredMark={false}
        layout="vertical"
      >
        <Form.Item name="teamName">
          <Input
            placeholder="팀명을 입력해주세요."
            onChange={handleTeamNameChange}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddTeamModal;
